import { forwardRef } from "react";

import { EnumSentFaxStatus } from "@/Common/Models/Faxing/EnumSentFaxStatus";
import { SentFaxUiStatus } from "@/Common/Models/UI/SentFaxUiStatus";
import { IconButton } from "@/DesignSystem";
import { IconVariant } from "@/DesignSystem/Atoms/Icons/Icon/Icon";
import { IconButtonSize } from "@/DesignSystem/Molecules/IconButton/IconButton";
import { SentFaxStatusHelper } from "@/Patient/Helpers/SentFaxStatusHelper";

import styles from "./FaxStatusIcon.module.scss";

import ArrowsClockwise from "@/DesignSystem/Atoms/Icons/Types/ArrowsClockwise/arrows-clockwise-regular.svg";
import CheckCircle from "@/DesignSystem/Atoms/Icons/Types/CheckCircle/check-circle-regular.svg";
import Warning from "@/DesignSystem/Atoms/Icons/Types/Warning/warning-regular.svg";
import XCircle from "@/DesignSystem/Atoms/Icons/Types/XCircle/x-circle-regular.svg";

interface IFaxStatusIconButtonProps {
    /**
     * The fax statuses for which to show an icon.
     */
    statuses: EnumSentFaxStatus[];

    /**
     * Whether the button is activated.
     */
    isClicked?: boolean;

    /**
     * The size of the icon to render.
     */
    iconSize?: IconButtonSize;

    /**
     * The HTML ID to set for the tooltip anchor element.
     */
    tooltipTriggerId?: string;

    /**
     * Handle a click on the icon container element.
     */
    onClick?: () => void;

    /**
     * The CSS classes to add to the icon container element.
     */
    iconButtonWrapperClasses?: string;
}

type IconAndColor = {
    icon: JSX.Element,
    variant: IconVariant
};

const statusIconAndColors: { [key: string]: IconAndColor } = {
    [SentFaxUiStatus.InProgress]: {
        icon: <ArrowsClockwise />,
        variant: "brand"
    },
    [SentFaxUiStatus.Sent]: {
        icon: <CheckCircle />,
        variant: "success"
    },
    [SentFaxUiStatus.Failed]: {
        icon: <XCircle />,
        variant: "error"
    },
    [SentFaxUiStatus.Error]: {
        icon: <Warning />,
        variant: "warning"
    }
};

const FaxStatusIconButton = forwardRef<HTMLDivElement, IFaxStatusIconButtonProps>((props, ref): JSX.Element => {
    const status = SentFaxStatusHelper.getOverallStatus(props.statuses);
    const statusDetails = statusIconAndColors[status];

    return (
        <div
            ref={ref}
            title={cacheStore.getSentFaxUiStatusName(status)}
            onClick={props.onClick}
            className={`${styles.iconButtonWrapper} ${props.iconButtonWrapperClasses}`}
        >
            <a data-tooltip-id={props.tooltipTriggerId} data-cy="send-fax-indicator-icon">
                <IconButton
                    category={props.isClicked ? "primary" : "tertiary"}
                    size={props.iconSize ?? "md"}
                    variant={statusDetails.variant}
                >
                    {statusDetails.icon}
                </IconButton>
            </a>
        </div>
    );
});

export { FaxStatusIconButton, statusIconAndColors };
